import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";

import { UserAuthenticatedLayout } from "@/layouts/AuthenticatedLayout/UserAuthenticatedLayout";
import { useAuthContext } from "@/modules/auth/hooks/useAuthContext";
import { authenticatedRouting } from "@/modules/router/routings/authenticatedRouting";
import { useGetDashboardTasksQuery } from "@/modules/tasks/components/hooks/useGetDashboardTasksQuery";
import { FirstLoadingPageView } from "@/modules/theme/components/FirstLoadingPageView";
import { Loader } from "@/modules/theme/components/Loader/Loader";
import { PageMeta } from "@/modules/theme/components/PageMeta";
import { Typography } from "@/modules/theme/components/Typography/Typography";
import { useTheme } from "@/modules/theme/hooks/useTheme";

import { DashboardSection } from "./components/DashboardSection";
import { EmptyDashboardView } from "./components/EmptyDashboardView";
import { createDashboardStyles } from "./createDashboardStyles";

const translationNamespace = "pages/DashboardPage";

export const DashboardPage = () => {
  const classes = createDashboardStyles(useTheme());
  const { t } = useTranslation(translationNamespace);
  const { hasAdminPrivilages, userCompany } = useAuthContext();
  const {
    data: tasks,
    isLoading: isTasksDataLoading,
    isSuccess: isTasksDataFetched,
  } = useGetDashboardTasksQuery();

  if (userCompany && !hasAdminPrivilages) {
    return <Redirect to={authenticatedRouting.buildUrl("tasks")} />;
  }

  const isTasksListEmpty = !tasks?.length && isTasksDataFetched;

  if (isTasksListEmpty) {
    return <EmptyDashboardView />;
  }

  if (isTasksDataLoading) {
    return <FirstLoadingPageView heading={t("Dashboard")} />;
  }

  const undoneTasks = tasks?.filter((task) => task.progress < 100);

  const toRateTasks = tasks?.filter(
    (task) => task.progress === 100 && !task.rated
  );
  const ratedTasks = tasks?.filter((task) => task.rated);

  return (
    <UserAuthenticatedLayout>
      <PageMeta title={t("Dashboard")} />
      <div className={classes.content}>
        <Typography className={classes.heading} variant="pageHeading">
          {t("Dashboard")}
        </Typography>
        {isTasksDataLoading ? (
          <div className={classes.loaderWrapper}>
            <Loader size={70} variant="primary" />
          </div>
        ) : (
          <>
            {toRateTasks?.length ? (
              <DashboardSection
                tasks={toRateTasks}
                heading={t("To rate")}
                done
              />
            ) : null}
            {undoneTasks?.length ? (
              <DashboardSection tasks={undoneTasks} heading={t("Undone")} />
            ) : null}

            {ratedTasks?.length ? (
              <DashboardSection tasks={ratedTasks} heading={t("Rated")} done />
            ) : null}
          </>
        )}
      </div>
    </UserAuthenticatedLayout>
  );
};
